/**
 * AgentToolCallCard — inline card for registry tool calls in the chat stream.
 *
 * Running  → bordered card with shimmer sweep. No content noise.
 * Done     → same card, border settles, solid icon signals completion.
 * Expanded → content reveals inside the same card — one unified component.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Flex, Text, Collapse, VStack, HStack, Button, Badge, Textarea, keyframes } from '@chakra-ui/react';

const githubArtifactReveal = keyframes`
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    max-height: 760px;
    transform: translateY(0);
  }
`;

// ── Labels — state-neutral (describe the action, not the ongoing state) ────────
function actionLabel(tool, params = {}) {
  switch (tool) {
    case 'web_search':
      return params.query ? `Search: "${params.query}"` : 'Web search';
    case 'search_market_intelligence':
      return params.query ? `Market search: "${params.query}"` : 'Market intelligence';
    case 'read_strategy_context':   return 'Read strategy context';
    case 'read_plan_learnings':     return 'Read plan learnings';
    case 'read_competitor_data':    return 'Read competitor data';
    case 'read_uploaded_file':
      return params.filename ? `Read ${params.filename}` : 'Read attached file';
    case 'prepare_github_change_request':
      return params.title ? `Prepare GitHub change: ${params.title}` : 'Prepare GitHub change';
    case 'create_github_change_request':
      return params.title ? `GitHub change: ${params.title}` : 'GitHub change request';
    case 'approve_learning': {
      const id = params.learning_id ? ` #${params.learning_id}` : '';
      const decision = params.decision || 'approve';
      if (decision === 'covered') return `Mark learning${id} covered`;
      if (decision === 'acknowledge') return `Acknowledge learning${id}`;
      if (decision === 'reject') return `Reject learning${id}`;
      const partial = params.selected_change_numbers?.length || params.selected_targets?.length;
      return `${partial ? 'Partially approve' : 'Approve'} learning${id}`;
    }
    case 'evaluate_learning_impact':
      return params.learning
        ? `Evaluate: "${String(params.learning).slice(0, 50)}"`
        : 'Evaluate learning impact';
    case 'create_agent_task': {
      if (params.capability === 'consult_agents') {
        const agentList = params.parameters?.agents?.length
          ? params.parameters.agents.map(a => a.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ')
          : 'specialists';
        return `Consulting ${agentList}`;
      }
      if (params.capability === 'connector_write') {
        const svc = params.parameters?.connector || 'connector';
        const tool = params.parameters?.tool || 'write';
        const action = tool.replace(`${svc}-`, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return `${svc.charAt(0).toUpperCase() + svc.slice(1)}: ${action}`;
      }
      const type = params.task_type || (params.capability
        ? params.capability.replace(/^update_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : 'task');
      return params.execute_now ? type : `Schedule: ${type}`;
    }
    case 'run_task':    return params.task_id ? `Run task #${params.task_id}`     : 'Run task';
    case 'get_task':    return params.task_id ? `Check task #${params.task_id}`   : 'Check task';
    case 'update_task': return params.task_id ? `Update task #${params.task_id}`  : 'Update task';
    case 'cancel_task': return params.task_id ? `Cancel task #${params.task_id}`  : 'Cancel task';
    case 'archive_task':return params.task_id ? `Archive task #${params.task_id}` : 'Archive task';
    default: {
      // update_* strategy patch tools — show a human label
      if (tool.startsWith('update_')) {
        const section = tool.replace(/^update_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return `Update ${section}`;
      }
      // Connector tools: notion-search → "Notion: Search", notion-create-database → "Notion: Create Database"
      if (tool.includes('-')) {
        const [service, ...rest] = tool.split('-');
        const action = rest.join(' ').replace(/\b\w/g, c => c.toUpperCase());
        const svc = service.charAt(0).toUpperCase() + service.slice(1);
        if (params.query) return `${svc}: ${action} "${String(params.query).slice(0, 40)}"`;
        if (params.id) return `${svc}: ${action}`;
        return `${svc}: ${action}`;
      }
      return tool.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
  }
}


const TOOL_ICONS = {
  web_search:                 'travel_explore',
  search_market_intelligence: 'manage_search',
  read_strategy_context:      'auto_stories',
  read_plan_learnings:        'history_edu',
  read_competitor_data:       'analytics',
  read_uploaded_file:         'draft',
  prepare_github_change_request:'difference',
  create_github_change_request:'code',
  consult_agents:             'group',
  connector_write:            'edit_note',
  approve_learning:           'fact_check',
  evaluate_learning_impact:   'psychology',
  create_agent_task:          'add_task',
  run_task:                   'play_circle',
  get_task:                   'info',
  update_task:                'edit',
  cancel_task:                'cancel',
  archive_task:               'archive',
};

function toolIcon(name, params = {}) {
  if (name === 'create_agent_task' && params.capability === 'generate_document') return 'description';
  if (name.startsWith('update_')) return 'edit_note';
  // Connector tools by service prefix
  if (name.startsWith('notion-')) return 'article';
  if (name.startsWith('askable-')) return 'forum';
  if (name.includes('-')) return 'cable'; // generic connector
  return TOOL_ICONS[name] || 'build';
}

// Params that are internal plumbing — never shown in the card
const SKIP_PARAMS = new Set([
  'plan_id', 'target_id', 'target_type', 'created_by', 'execute_now',
  'requires_approval', 'priority', 'scheduled_at', '_context',
]);

// Result keys to skip — internal or redundant
const SKIP_RESULT = new Set([
  '_context', 'executed', 'requires_approval', 'scheduled_at', 'status',
  'capability', 'id', 'rawValue', 'reactField', 'result', 'error',
]);

function formatValue(v) {
  if (v === null || v === undefined) return null;
  if (Array.isArray(v)) return `${v.length} item${v.length !== 1 ? 's' : ''}`;
  if (typeof v === 'object') return `${Object.keys(v).length} field${Object.keys(v).length !== 1 ? 's' : ''}`;
  const s = String(v);
  return s.length > 60 ? s.slice(0, 60) + '…' : s;
}

function CompactRow({ label, value }) {
  const display = formatValue(value);
  if (!display) return null;
  return (
    <HStack align="flex-start" spacing={2}>
      <Text fontSize="10px" color="gray.400" flexShrink={0} minW="80px" textTransform="capitalize">
        {label.replace(/_/g, ' ')}
      </Text>
      <Text fontSize="10px" color="gray.600" wordBreak="break-word" flex={1}>{display}</Text>
    </HStack>
  );
}

function githubPayloadFromResult(result) {
  if (!result || typeof result !== 'object') return {};
  const nested = result.result && typeof result.result === 'object' ? result.result : null;
  return nested || result;
}

function githubStatusLabel(status, payload = {}) {
  const state = payload.state || status;
  if (payload.error || state === 'failed' || state === 'error') return 'Failed';
  if (state === 'waiting_for_approval') return 'Needs review';
  if (state === 'waiting_for_user') return 'Changes requested';
  if (state === 'completed') return 'Done';
  if (state === 'cancelled') return 'Cancelled';
  if (state === 'rejected') return 'Rejected';
  if (state === 'blocked') return 'Blocked';
  return state ? String(state).replace(/_/g, ' ') : 'Needs review';
}

function githubStatusColor(status, payload = {}) {
  const state = payload.state || status;
  if (payload.error || state === 'failed' || state === 'error') return 'red';
  if (state === 'completed') return 'green';
  if (state === 'cancelled' || state === 'rejected') return 'gray';
  if (state === 'blocked' || state === 'waiting_for_user') return 'orange';
  return 'orange';
}

const GITHUB_CHANGE_TOOLS = new Set(['prepare_github_change_request', 'create_github_change_request']);

export function isGithubChangeToolName(tool) {
  return GITHUB_CHANGE_TOOLS.has(tool);
}

export function shouldRenderGithubOutcomeCard(toolCall) {
  if (!isGithubChangeToolName(toolCall?.tool) || !toolCall?.result || toolCall.status === 'running') {
    return false;
  }

  const payload = githubPayloadFromResult(toolCall.result);
  const state = payload.state || toolCall.result?.status || toolCall.status;
  if (payload.error || state === 'failed' || state === 'error') {
    return false;
  }

  return Boolean(
    toolCall.result?.id ||
    payload.id ||
    payload.proposed_branch ||
    payload.pull_request_url ||
    (Array.isArray(payload.proposed_files) && payload.proposed_files.length > 0)
  );
}

function isGithubCreditBlockedError(value) {
  const text = String(value || '').toLowerCase();
  return Boolean(text) && (
    text.includes('task limit reached') ||
    text.includes('not enough credits') ||
    text.includes('insufficient_credits') ||
    text.includes('insufficient agent task credits')
  );
}

function normaliseGithubToolResult(toolCall, overrideResult = null) {
  if (!toolCall) return null;
  const baseResult = overrideResult || toolCall.result;
  if (!baseResult || typeof baseResult !== 'object') return null;
  const payload = githubPayloadFromResult(baseResult);
  if (isGithubCreditBlockedError(payload?.error || baseResult.error)) {
    const cleanedPayload = { ...payload };
    delete cleanedPayload.error;
    return {
      ...baseResult,
      status: 'waiting_for_approval',
      result: {
        ...cleanedPayload,
        state: 'waiting_for_approval',
        credit_blocked: true,
      },
    };
  }
  return {
    ...baseResult,
    status: baseResult.status || payload?.state || toolCall.status,
  };
}

function buildGithubResultFromTask(task, fallback = null) {
  if (!task || typeof task !== 'object') return fallback;
  const taskResult = task.result && typeof task.result === 'object' ? task.result : {};
  const taskParams = task.parameters && typeof task.parameters === 'object' ? task.parameters : {};
  const fallbackPayload = githubPayloadFromResult(fallback);
  const state = taskResult.state || task.status || fallback?.status || fallbackPayload.state;
  return {
    ...(fallback || {}),
    id: task.id || fallback?.id,
    status: state,
    summary: taskResult.summary || task.reasoning || fallback?.summary,
    result: {
      ...fallbackPayload,
      ...taskParams,
      ...taskResult,
      state,
    },
  };
}

function dispatchGithubCreditBlockedReply() {
  window.dispatchEvent(new CustomEvent('pabs:assistantMessage', {
    detail: {
      source: 'github_change_credit_block',
      text: [
        "I can’t open this GitHub pull request yet because we’re out of task credits.",
        "- [Top up credits](/topup) if you want me to open it now. This approval needs 1 task credit.",
        "- [Upgrade to Pro](/upgrade) if these agent jobs are becoming part of your regular workflow.",
        "- [Check your usage](/plan-usage) to see the current balance and reset timing.",
        "Once credits are back, approve the card again and I’ll pick it up from there.",
      ].join('\n\n'),
    },
  }));
}

function dispatchGithubDecisionReply(decision, result, assistantMessage = '') {
  if (assistantMessage && String(assistantMessage).trim()) {
    window.dispatchEvent(new CustomEvent('pabs:assistantMessage', {
      detail: {
        source: 'github_change_decision',
        text: String(assistantMessage).trim(),
      },
    }));
    return;
  }

  const payload = githubPayloadFromResult(result);
  const state = payload.state || result?.status;
  const prUrl = payload.pull_request_url;
  const prNumber = payload.pull_request_number;
  let text = '';

  if (decision === 'approve') {
    if (state === 'completed' && prUrl) {
      text = `Done, the pull request is open in GitHub${prNumber ? ` as #${prNumber}` : ''}: ${prUrl}`;
    } else if (state === 'completed') {
      text = 'Done, the pull request is open in GitHub.';
    } else if (state === 'blocked') {
      text = payload.blocked_reason || 'I approved the GitHub change request, but I need one more step before I can open the pull request.';
    } else {
      text = 'I approved the GitHub change request and updated its status.';
    }
  } else if (decision === 'request_changes') {
    text = 'Got it, I paused this GitHub change request for revisions before opening a pull request.';
  } else if (decision === 'reject') {
    text = 'Rejected. I will not create a branch, commit, or pull request for that change.';
  }

  if (!text) return;
  window.dispatchEvent(new CustomEvent('pabs:assistantMessage', {
    detail: {
      source: 'github_change_decision',
      text,
    },
  }));
}

export function GithubReviewCard({ result, onDecision, isUpdating, reviewMode, setReviewMode, feedback, setFeedback }) {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const payload = githubPayloadFromResult(result);
  const taskId = result?.id;
  const repoValue = payload.repository || result?.repository || 'GitHub repository';
  const repo = typeof repoValue === 'object'
    ? (repoValue.fullName || repoValue.full_name || repoValue.name || 'GitHub repository')
    : String(repoValue || 'GitHub repository');
  const repoUrl = payload.repository_url
    || (typeof repoValue === 'object' ? (repoValue.htmlUrl || repoValue.html_url) : null)
    || (repo.includes('/') ? `https://github.com/${repo}` : null);
  const files = Array.isArray(payload.proposed_files) ? payload.proposed_files : [];
  const changedFiles = Array.isArray(payload.files_changed) ? payload.files_changed : [];
  const additions = Number(payload.additions) || files.reduce((sum, file) => sum + (Number(file.additions) || 0), 0);
  const deletions = Number(payload.deletions) || files.reduce((sum, file) => sum + (Number(file.deletions) || 0), 0);
  const state = payload.state || result?.status;
  const isWaiting = state === 'waiting_for_approval' || result?.status === 'waiting_for_approval';
  const prUrl = payload.pull_request_url;
  const primaryFile = files[0]?.path || changedFiles[0]?.path || 'proposed change';
  const changeText = payload.request || result?.summary || `Update ${primaryFile}.`;
  const shouldClampChangeText = String(changeText || '').length > 280;
  const branchName = payload.proposed_branch || payload.branch;
  const fileList = files.length > 0
    ? files.slice(0, 3).map(file => file.path).join(', ')
    : changedFiles.slice(0, 3).map(file => file.path).filter(Boolean).join(', ');

  return (
    <Box mt={3} mb={2} border="1px solid" borderColor="gray.200" borderRadius="10px" overflow="hidden" bg="white">
      <Box px={3} py={3} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
        <HStack spacing={2} align="center" mb={2}>
          <Box as="span" className="material-icons-outlined" style={{ fontSize: '15px', color: '#1A202C' }}>code</Box>
          <Text fontSize="13px" fontWeight="700" color="gray.800" flex={1} minW={0} noOfLines={1}>
            GitHub change request
          </Text>
          <Badge colorScheme={githubStatusColor(result?.status, payload)} fontSize="8px" flexShrink={0}>
            {githubStatusLabel(result?.status, payload)}
          </Badge>
        </HStack>
        <Box
          as={prUrl ? 'a' : 'span'}
          href={prUrl || undefined}
          target={prUrl ? '_blank' : undefined}
          rel={prUrl ? 'noopener noreferrer' : undefined}
          display="inline-flex"
          alignItems="center"
          border="1px solid"
          borderColor="gray.300"
          borderRadius="999px"
          px={2.5}
          py={1}
          bg="white"
          color="gray.500"
          fontSize="11px"
          fontWeight="700"
          lineHeight="1"
          textDecoration="none"
          cursor={prUrl ? 'pointer' : 'default'}
          title={prUrl ? 'Open pull request in GitHub' : 'Pull request will be created after approval'}
          _hover={prUrl ? { borderColor: 'gray.500', color: 'gray.700' } : undefined}
        >
          Pull request
          <Box as="span" color="green.600" ml={1}>+{additions}</Box>
          <Box as="span" color="red.500" ml={1}>-{deletions}</Box>
        </Box>
      </Box>

      <VStack align="stretch" spacing={3} px={3} py={3}>
        <Box>
          <Text
            fontSize="12px"
            color="gray.800"
            lineHeight="1.55"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            noOfLines={shouldClampChangeText && !detailsExpanded ? 5 : undefined}
          >
            {changeText}
          </Text>
          {shouldClampChangeText && (
            <Button
              size="xs"
              variant="ghost"
              colorScheme="gray"
              mt={1.5}
              px={0}
              h="auto"
              minW="auto"
              fontSize="11px"
              fontWeight="600"
              onClick={() => setDetailsExpanded(expanded => !expanded)}
              _hover={{ bg: 'transparent', color: 'gray.900', textDecoration: 'underline' }}
            >
              {detailsExpanded ? 'Show less' : 'Expand to read more'}
            </Button>
          )}
        </Box>

        <Text fontSize="12px" color="gray.600" lineHeight="1.55" wordBreak="break-word">
          {fileList ? `Touches ${fileList}` : `Touches ${primaryFile}`}
          {repo ? ' in ' : ''}
          {repoUrl ? (
            <Box
              as="a"
              href={repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="blue.600"
              fontWeight="600"
              textDecoration="underline"
              wordBreak="break-word"
            >
              {repo}
            </Box>
          ) : repo}
          {branchName ? (
            <>
              {' '}on <Box as="span" fontFamily="mono" wordBreak="break-all">{branchName}</Box>
            </>
          ) : null}
          .
        </Text>

        {payload.review_feedback && (
          <Box borderTop="1px solid" borderColor="gray.100" pt={2}>
            <Text fontSize="12px" color="gray.700" lineHeight="1.55">
              Requested changes: {payload.review_feedback}
            </Text>
          </Box>
        )}

        {reviewMode === 'request_changes' && isWaiting && (
          <Box borderTop="1px solid" borderColor="gray.100" pt={2}>
            <Text fontSize="9px" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>What changes are needed?</Text>
            <Textarea
              size="sm"
              value={feedback}
              onChange={(event) => setFeedback(event.target.value)}
              placeholder="Describe what Pabs should change before opening the pull request"
              minH="76px"
              resize="vertical"
              fontSize="12px"
              borderRadius="0"
              isDisabled={isUpdating}
            />
            <HStack spacing={2} mt={2}>
              <Button size="xs" colorScheme="gray" isLoading={isUpdating === 'request_changes'} isDisabled={Boolean(isUpdating) || !feedback.trim()} onClick={() => onDecision('request_changes', feedback)}>
                Request changes
              </Button>
              <Button size="xs" variant="ghost" isDisabled={isUpdating} onClick={() => { setReviewMode(null); setFeedback(''); }}>
                Cancel
              </Button>
            </HStack>
          </Box>
        )}

        {reviewMode === 'reject' && isWaiting && (
          <Box borderTop="1px solid" borderColor="gray.100" pt={2}>
            <HStack spacing={2}>
              <Button size="xs" colorScheme="red" isLoading={isUpdating === 'reject'} isDisabled={Boolean(isUpdating)} onClick={() => onDecision('reject')}>
                Reject
              </Button>
              <Button size="xs" variant="ghost" isDisabled={isUpdating} onClick={() => setReviewMode(null)}>
                Cancel
              </Button>
            </HStack>
          </Box>
        )}

        {!reviewMode && (
          <HStack spacing={2} borderTop="1px solid" borderColor="gray.100" pt={2} wrap="wrap">
            <Button size="xs" colorScheme="green" isLoading={isUpdating === 'approve'} isDisabled={Boolean(isUpdating) || !taskId || !isWaiting} onClick={() => onDecision('approve')}>
              Approve
            </Button>
            <Button size="xs" variant="outline" colorScheme="gray" isDisabled={Boolean(isUpdating) || !taskId || !isWaiting} onClick={() => setReviewMode('request_changes')}>
              Request changes
            </Button>
            <Button size="xs" variant="ghost" colorScheme="red" isDisabled={Boolean(isUpdating) || !taskId || !isWaiting} onClick={() => setReviewMode('reject')}>
              Reject
            </Button>
          </HStack>
        )}

      </VStack>
    </Box>
  );
}

export function GithubOutcomeCard({ toolCall }) {
  const [decisionResult, setDecisionResult] = useState(null);
  const [decisionUpdating, setDecisionUpdating] = useState(null);
  const [githubReviewMode, setGithubReviewMode] = useState(null);
  const [githubReviewFeedback, setGithubReviewFeedback] = useState('');

  const cardResult = normaliseGithubToolResult(toolCall, decisionResult);
  const taskId = cardResult?.id;

  const refreshTaskState = useCallback(async () => {
    if (!taskId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/agent/tasks/${taskId}/detail`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) return;
      const payload = await response.json().catch(() => ({}));
      if (!payload.task) return;
      setDecisionResult(previous => buildGithubResultFromTask(
        payload.task,
        previous || normaliseGithubToolResult(toolCall)
      ));
    } catch (error) {
      console.warn('Could not refresh GitHub change request state:', error);
    }
  }, [taskId, toolCall]);

  useEffect(() => {
    if (!taskId) return undefined;
    const handleExternalDecision = (event) => {
      const detail = event.detail || {};
      if (Number(detail.taskId) !== Number(taskId)) return;
      const fallbackResult = normaliseGithubToolResult(toolCall);
      setDecisionResult({
        ...(fallbackResult || {}),
        id: detail.taskId,
        status: detail.status || detail.result?.state || fallbackResult?.status,
        result: detail.result || githubPayloadFromResult(fallbackResult),
      });
      setGithubReviewMode(null);
      setGithubReviewFeedback('');
    };
    window.addEventListener('pabs:githubChangeDecision', handleExternalDecision);
    window.addEventListener('pabs:agentTasksUpdated', refreshTaskState);
    refreshTaskState();
    return () => {
      window.removeEventListener('pabs:githubChangeDecision', handleExternalDecision);
      window.removeEventListener('pabs:agentTasksUpdated', refreshTaskState);
    };
  }, [taskId, toolCall, refreshTaskState]);

  const handleGithubDecision = useCallback(async (decision, feedback = '') => {
    if (!taskId || decisionUpdating) return;
    setDecisionUpdating(decision);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/agent/tasks/${taskId}/github-change-decision`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, feedback }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(payload.error || 'Could not update GitHub change request');
        error.status = response.status;
        throw error;
      }
      const nextResult = {
        ...(cardResult || {}),
        id: payload.task?.id || taskId,
        status: payload.task?.status || payload.result?.state,
        result: payload.result || {},
      };
      setDecisionResult(nextResult);
      setGithubReviewMode(null);
      setGithubReviewFeedback('');
      window.dispatchEvent(new CustomEvent('pabs:githubChangeDecision', {
        detail: {
          taskId: payload.task?.id || taskId,
          status: payload.task?.status || payload.result?.state,
          result: payload.result || {},
        },
      }));
      window.dispatchEvent(new CustomEvent('pabs:agentTasksUpdated'));
      dispatchGithubDecisionReply(decision, nextResult, payload.assistant_message);
    } catch (error) {
      console.error('GitHub change decision failed:', error);
      if (error.status === 402 || isGithubCreditBlockedError(error.message)) {
        setGithubReviewMode(null);
        setGithubReviewFeedback('');
        dispatchGithubCreditBlockedReply();
        return;
      }
      setDecisionResult({
        ...(cardResult || {}),
        result: {
          ...(githubPayloadFromResult(cardResult) || {}),
          error: error.message || 'Could not update GitHub change request',
        },
      });
    } finally {
      setDecisionUpdating(null);
    }
  }, [taskId, decisionUpdating, cardResult]);

  if (!cardResult) return null;

  return (
    <Box
      overflow="hidden"
      animation={`${githubArtifactReveal} 220ms cubic-bezier(0.16, 1, 0.3, 1) both`}
      style={{ transformOrigin: 'top' }}
    >
      <GithubReviewCard
        result={cardResult}
        onDecision={handleGithubDecision}
        isUpdating={decisionUpdating}
        reviewMode={githubReviewMode}
        setReviewMode={setGithubReviewMode}
        feedback={githubReviewFeedback}
        setFeedback={setGithubReviewFeedback}
      />
    </Box>
  );
}

// Pick the most useful params for a given tool — skip infrastructure, limit to 3
function getDisplayParams(tool, params) {
  const entries = Object.entries(params || {})
    .filter(([k, v]) => !SKIP_PARAMS.has(k) && v !== undefined && v !== null && v !== false && v !== '');

  // Tool-specific priority ordering
  const priority = {
    web_search:              ['query', 'max_results'],
    search_market_intelligence: ['query', 'max_results'],
    create_agent_task:       ['task_type', 'capability', 'reasoning'],
    approve_learning:         ['learning_id', 'decision', 'selected_targets'],
    evaluate_learning_impact:['learning'],
  };
  const order = priority[tool] || [];
  const sorted = [
    ...order.map(k => entries.find(([ek]) => ek === k)).filter(Boolean),
    ...entries.filter(([k]) => !order.includes(k)),
  ];
  return sorted.slice(0, 3);
}

// Extract readable text from a sub-agent streaming response.
// Sub-agents output JSON ({ "summary": "...", "findings": [...] }) token by token.
// While streaming we only have a partial fragment — extract the summary prose.
function extractStreamingDisplay(raw) {
  if (!raw) return '';
  // If JSON has started building, try to pull summary value
  const summaryMatch = raw.match(/"summary"\s*:\s*"([\s\S]*)/);
  if (summaryMatch) {
    let text = summaryMatch[1];
    // Truncate at the next JSON key or closing quote+brace
    text = text.replace(/",?\s*"(findings|confidence|gap|clarification_needed)"[\s\S]*$/, '');
    text = text.replace(/"?\s*}?\s*$/, '');
    return text.trim();
  }
  // Before JSON starts (tool calls happening) — strip leading { and key patterns
  const stripped = raw.replace(/^[\s{]*/, '').replace(/"?\w+"?\s*:\s*"?/, '');
  return stripped.trim() || raw.trim();
}

// Try to extract a short readable summary from a connector MCP text result
function summariseConnectorResult(text) {
  if (!text || typeof text !== 'string') return null;
  // Try to parse as JSON and extract a human-readable summary
  try {
    const parsed = JSON.parse(text);
    // Notion search returns { pages: [...] }
    if (Array.isArray(parsed.pages)) {
      const count = parsed.pages.length;
      const titles = parsed.pages.slice(0, 3).map(p =>
        p.properties?.title?.title?.[0]?.plain_text ||
        p.properties?.Name?.title?.[0]?.plain_text ||
        p.title ||
        p.id
      ).filter(Boolean);
      const label = count === 1 ? '1 page' : `${count} pages`;
      return titles.length ? `${label}: ${titles.join(', ')}` : label;
    }
    // Generic results array
    if (Array.isArray(parsed.results)) {
      const count = parsed.results.length;
      return `${count} result${count !== 1 ? 's' : ''}`;
    }
    // Single object with a title or name
    if (parsed.title) return parsed.title;
    if (parsed.name) return parsed.name;
    // Object — just show key count
    const keys = Object.keys(parsed).filter(k => !['id','object'].includes(k));
    if (keys.length) return `${keys.length} field${keys.length !== 1 ? 's' : ''} returned`;
  } catch {}
  // Plain text — take first meaningful line (skip JSON-looking lines)
  const lines = text.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('{') && !l.startsWith('['));
  const first = lines[0] || text.trim().slice(0, 100);
  return first.length > 100 ? first.slice(0, 100) + '…' : first;
}


function getDisplayResult(result, tool) {
  if (!result) return [];

  // consult_agents: show specialist count or "no specialists"
  if (tool === 'create_agent_task' && Array.isArray(result.results)) {
    if (result.results.length === 0) return [['specialists', 'none consulted']];
    return [['specialists', `${result.results.length} response${result.results.length !== 1 ? 's' : ''}`]];
  }
  // connector_write: show connector + outcome
  if (tool === 'create_agent_task' && result.connector && result.tool) {
    const summary = result.result ? summariseConnectorResult(result.result) : null;
    return [
      ['connector', `${result.connector} · ${result.tool.replace(`${result.connector}-`, '').replace(/-/g, ' ')}`],
      ...(summary ? [['result', summary]] : []),
    ];
  }

  // Connector tools: result.result is a string from the MCP server — never fall through to generic loop
  if (tool && tool.includes('-')) {
    const raw = typeof result.result === 'string' ? result.result : (result.result ? JSON.stringify(result.result) : null);
    const summary = raw ? summariseConnectorResult(raw) : null;
    return summary ? [['result', summary]] : [];
  }

  const entries = [];
  const nestedResult =
    result.result && typeof result.result === 'object' && !Array.isArray(result.result)
      ? result.result
      : null;
  const displaySource = nestedResult || result;

  if (displaySource.value) {
    entries.push(['summary', displaySource.value]);
  }

  if (displaySource.rawValue && displaySource.reactField === 'finalProgramSequence') {
    const count = Array.isArray(displaySource.rawValue) ? displaySource.rawValue.length : null;
    entries.push(['updated', count ? `${count} program${count !== 1 ? 's' : ''}` : 'Action plan']);
  }

  if (result.section && !entries.some(([key]) => key === 'section')) {
    entries.push(['section', result.section]);
  }

  Object.entries(displaySource)
    .filter(([k, v]) =>
      !SKIP_RESULT.has(k) &&
      k !== 'value' &&
      v !== undefined &&
      v !== null
    )
    .forEach(([k, v]) => entries.push([k, v]));

  return entries.slice(0, 4);
}

// ── Main card ─────────────────────────────────────────────────────────────────
export default function AgentToolCallCard({ toolCall, onTaskApply, onTaskSkip }) {
  const [expanded, setExpanded] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [flash, setFlash] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [mounted, setMounted] = useState(false);

  const { tool, userDescription, params = {}, result, status = 'running', streamContent } = toolCall || {};
  const cardResult = result;

  // Approval is now conversational. The card stays as an audit trail, but the
  // user confirms by replying "yes/ok/continue"; mixing inline Apply with chat
  // confirmations created two competing state machines.
  const SHOW_INLINE_APPROVAL_CONTROLS = false;

  // A task card can show Apply/Skip only if inline controls are re-enabled.
  const taskId = cardResult?.id;
  const awaitingConversationApproval = tool === 'create_agent_task' && cardResult?.requires_approval === true && !applied && !skipped;
  const needsApproval = SHOW_INLINE_APPROVAL_CONTROLS && awaitingConversationApproval;

  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const mountedStatusRef = useRef(status);
  useEffect(() => {
    if (status === 'success' && mountedStatusRef.current !== 'success') {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1200);
      return () => clearTimeout(t);
    }
  }, [status]);

  const handleApply = useCallback(async (e) => {
    e.stopPropagation();
    if (!taskId || applying) return;
    setApplying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/agent/tasks/${taskId}/run`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        setApplied(true);
        setFlash(true);
        setTimeout(() => setFlash(false), 1200);
        if (onTaskApply) onTaskApply(taskId, data);
        // Signal plan panel to refresh for update_* and action plan Tier B capabilities
        const TIER_B_CAPS = new Set(['tool_duration_editing','add_tools_to_stages','remove_tools_from_stages','modify_tools_content','bulk_actions']);
        if (data?.capability?.startsWith?.('update_') || TIER_B_CAPS.has(data?.capability) || data?.section) {
          window.dispatchEvent(new CustomEvent('pabs-plan-updated'));
        }
      } else {
        console.error('Task apply failed:', data.error);
      }
    } catch (err) {
      console.error('Task apply error:', err);
    }
    setApplying(false);
  }, [taskId, applying, onTaskApply]);

  const handleSkip = useCallback((e) => {
    e.stopPropagation();
    setSkipped(true);
    if (onTaskSkip) onTaskSkip(taskId);
  }, [taskId, onTaskSkip]);

  const label       = actionLabel(tool, params);
  const isRunning   = status === 'running';
  const isError     = status === 'error';
  const isDone      = status === 'success';
  const canExpand   = isDone || isError;
  const displayParams = getDisplayParams(tool, params);
  const displayResult = getDisplayResult(cardResult, tool);
  const documentResult = tool === 'create_agent_task' && params.capability === 'generate_document'
    ? cardResult?.result
    : null;
  const hasDetail   = canExpand && (displayParams.length > 0 || displayResult.length > 0);

  if (!toolCall) return null;

  // Border and background transitions for the unified card
  const borderColor = flash
    ? '#68D391'                          // green flash
    : applied
    ? '#68D391'                          // green when applied
    : awaitingConversationApproval
    ? '#90CDF4'                          // blue when awaiting approval
    : isError
    ? '#FEB2B2'                          // red for error
    : expanded
    ? '#CBD5E0'                          // slightly stronger when open
    : '#E2E8F0';                         // resting state — always bordered

  const bg = (flash || applied) ? '#F0FFF4' : awaitingConversationApproval ? '#EBF8FF' : 'white';

  return (
    <Box
      mt="6px" mb={1} w="100%"
      borderRadius="8px"
      border="1px solid"
      borderColor={borderColor}
      bg={bg}
      overflow="hidden"
      position="relative"
      style={{
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.985)',
        transition: [
          'opacity 0.22s ease',
          'transform 0.24s cubic-bezier(0.2,0.8,0.2,1)',
          'border-color 0.5s ease',
          'background-color 0.5s ease',
        ].join(', '),
      }}
    >
      {/* Shimmer sweep while running */}
      {isRunning && <Box className="agent-tool-shimmer-bar" />}

      {/* Header row */}
      <Flex
        px={3} py="8px" align="center" gap="8px"
        cursor={canExpand ? 'pointer' : 'default'}
        onClick={() => canExpand && setExpanded(o => !o)}
        _hover={canExpand ? { bg: 'gray.50' } : {}}
        style={{ transition: 'background 0.15s' }}
      >
        <Box as="span"
          className={isDone || isError ? 'material-icons' : 'material-icons-outlined'}
          style={{
            fontSize: '14px',
            color: isRunning ? '#A0AEC0' : isError ? '#E53E3E' : '#1A202C',
            flexShrink: 0,
            transition: 'color 0.4s ease',
          }}
        >
          {isRunning ? toolIcon(tool, params) : isError ? 'error' : 'check_circle'}
        </Box>

        <Text
          fontSize="12px"
          color={isRunning ? 'gray.500' : 'gray.700'}
          fontWeight={isDone ? '500' : '400'}
          flex={1}
          noOfLines={1}
          style={{ transition: 'color 0.4s ease' }}
        >
          {label}
        </Text>

        {/* Inline approval controls are disabled; confirmation happens in chat. */}
        {needsApproval && (
          <HStack spacing="6px" flexShrink={0} onClick={e => e.stopPropagation()}>
            <Button
              size="xs" colorScheme="blue" variant="solid"
              fontSize="11px" h="22px" px={2}
              isLoading={applying}
              onClick={handleApply}
            >
              Apply
            </Button>
            <Button
              size="xs" variant="ghost" colorScheme="gray"
              fontSize="11px" h="22px" px={2}
              onClick={handleSkip}
            >
              Skip
            </Button>
          </HStack>
        )}

        {applied && (
          <Text fontSize="11px" color="green.500" flexShrink={0} fontWeight="500">Applied</Text>
        )}
        {skipped && (
          <Text fontSize="11px" color="gray.400" flexShrink={0}>Skipped</Text>
        )}
        {awaitingConversationApproval && !needsApproval && (
          <Text fontSize="11px" color="blue.500" flexShrink={0} fontWeight="500">Confirm in chat</Text>
        )}

        {isDone && documentResult?.destination && (
          <Button
            size="xs"
            variant="ghost"
            colorScheme="blue"
            fontSize="11px"
            h="22px"
            px={2}
            flexShrink={0}
            onClick={e => {
              e.stopPropagation();
              const url = new URL(documentResult.destination, window.location.origin);
              const documentId = Number(url.searchParams.get('document'));
              if (documentId) {
                window.dispatchEvent(new CustomEvent('pabs:openDocument', {
                  detail: { documentId },
                }));
              }
            }}
          >
            Open document
          </Button>
        )}

        {canExpand && hasDetail && !needsApproval && !applied && !skipped && (
          <Box as="span" className="material-icons-outlined"
            style={{
              fontSize: '16px',
              color: '#A0AEC0',
              flexShrink: 0,
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}>
            expand_more
          </Box>
        )}
      </Flex>

      {/* Inline streaming shell — consult_agents task running via create_agent_task */}
      {status === 'running' && streamContent && tool === 'create_agent_task' && params.capability === 'consult_agents' && (
        <Box
          px={3} py="10px"
          borderTop="1px solid"
          borderColor="gray.100"
          bg="gray.50"
          maxH="180px"
          overflowY="auto"
        >
          <Text fontSize="11px" color="gray.600" lineHeight="1.7" whiteSpace="pre-wrap" fontFamily="inherit">
            {extractStreamingDisplay(streamContent)}
          </Text>
        </Box>
      )}

      {/* Expanded content */}
      <Collapse in={expanded} animateOpacity>
        <Box px={3} pt="10px" pb="8px" borderTop="1px solid" borderColor="gray.100" bg="gray.50">
          <VStack align="stretch" spacing="10px">

            {/* Description */}
            {userDescription && (
              <Text fontSize="11px" color="gray.500" lineHeight="1.5">{userDescription}</Text>
            )}

            {/* Params */}
            {displayParams.length > 0 && (
              <Box>
                <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb="5px">Params</Text>
                <VStack align="stretch" spacing="3px">
                  {displayParams.map(([k, v]) => <CompactRow key={k} label={k} value={v} />)}
                </VStack>
              </Box>
            )}

            {/* Handler */}
            <Box>
              <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb="5px">Handler</Text>
              <HStack spacing={2}>
                <Box
                  px="6px" py="2px" borderRadius="4px"
                  bg={isError ? '#FFF5F5' : '#F0FFF4'}
                  border="1px solid"
                  borderColor={isError ? '#FEB2B2' : '#9AE6B4'}
                >
                  <Text fontSize="10px" fontWeight="600" color={isError ? 'red.500' : 'green.600'}>
                    {isError ? 'Failed' : 'Success'}
                  </Text>
                </Box>
                <Text fontSize="10px" color="gray.500">{tool}</Text>
              </HStack>
            </Box>

            {/* Result */}
            {(displayResult.length > 0 || (isError && result?.error)) && (
              <Box>
                <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb="5px">Result</Text>
                {isError && result?.error
                  ? <Text fontSize="10px" color="red.500" fontFamily="mono">{String(result.error).slice(0, 120)}</Text>
                  : <VStack align="stretch" spacing="3px">
                      {displayResult.map(([k, v]) => <CompactRow key={k} label={k} value={v} />)}
                    </VStack>
                }
              </Box>
            )}

            {/* Raw JSON toggle */}
            <Box>
              <Button
                size="xs" variant="ghost" colorScheme="gray"
                fontSize="10px" h="18px" px={2} mt={1}
                onClick={e => { e.stopPropagation(); setShowRaw(o => !o); }}
              >
                {showRaw ? 'Hide raw' : 'View raw'}
              </Button>
              {showRaw && (
                <Box
                  mt={2} p={2} borderRadius="6px"
                  bg="#1A202C" overflowX="auto"
                >
                  <pre style={{ fontSize: '10px', color: '#68D391', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify({ params, result: cardResult }, null, 2)}
                  </pre>
                </Box>
              )}
            </Box>

          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
}
